import React, { useState, useEffect } from 'react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal } from '../components/adminModals';
import {
    Copy,
    Check,
    CheckCircle,
    XCircle,
    FileText,
    Banknote,
    User,
    Trash2,
} from 'lucide-react';
import {
    reviewerPaymentService,
    ReviewerPaymentItem,
    BankAccountDetail,
    ReviewerPaymentRecord,
    ZERO_GUID,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';

function ReviewerPayment() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    // Only DESS may create or delete payment records; everyone else is read-only.
    const isDESS = user?.roleId === 9 || user?.role === 'dess';

    const [payments, setPayments] = useState<ReviewerPaymentItem[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Search/filter state
    const [search, setSearch] = useState('');

    // ── Account Details modal state ──────────────────────────────────────────
    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
    const [accountTarget, setAccountTarget] = useState<ReviewerPaymentItem | null>(null);
    const [accountDetail, setAccountDetail] = useState<BankAccountDetail | null>(null);
    const [loadingAccount, setLoadingAccount] = useState(false);
    const [copied, setCopied] = useState(false);

    // ── Create Payment modal state ───────────────────────────────────────────
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createTarget, setCreateTarget] = useState<ReviewerPaymentItem | null>(null);
    const [createAccount, setCreateAccount] = useState<BankAccountDetail | null>(null);
    const [loadingCreateAccount, setLoadingCreateAccount] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState({
        amount: '',
        datePaid: '',
        paymentReference: '',
        notes: '',
    });

    // ── View Payment modal state ─────────────────────────────────────────────
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewTarget, setViewTarget] = useState<ReviewerPaymentItem | null>(null);
    const [paymentRecord, setPaymentRecord] = useState<ReviewerPaymentRecord | null>(null);
    const [loadingRecord, setLoadingRecord] = useState(false);

    // ── Delete Payment modal state ───────────────────────────────────────────
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ReviewerPaymentItem | null>(null);
    const [deleteRecord, setDeleteRecord] = useState<ReviewerPaymentRecord | null>(null);
    const [loadingDeleteRecord, setLoadingDeleteRecord] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // ── Fetch payments ─────────────────────────────────────────────────────────

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchPayments();
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, search]);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const response = await reviewerPaymentService.getReadyForReviewerPayment({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
            });

            if (response.isSuccess && response.data) {
                setPayments(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch reviewer payments',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching reviewer payments',
            });
        } finally {
            setLoading(false);
        }
    };

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setSearch(query);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            showNotification('error', { title: 'Copy failed', message: 'Could not copy to clipboard.' });
        }
    };

    // ── View Account Details ─────────────────────────────────────────────────

    const openAccountModal = async (row: ReviewerPaymentItem) => {
        setAccountTarget(row);
        setAccountDetail(null);
        setIsAccountModalOpen(true);
        setLoadingAccount(true);
        try {
            const res = await reviewerPaymentService.getBankAccountDetailByUserId(row.reviewerId);
            if (res.isSuccess && res.data) {
                setAccountDetail(res.data);
            }
            // No record → the modal's empty-state text handles it (no popup).
        } catch {
            // Leave accountDetail null so the empty-state text shows.
        } finally {
            setLoadingAccount(false);
        }
    };

    const closeAccountModal = () => {
        setIsAccountModalOpen(false);
        setAccountTarget(null);
        setAccountDetail(null);
        setCopied(false);
    };

    // ── Create Payment Record ────────────────────────────────────────────────

    const openCreateModal = async (row: ReviewerPaymentItem) => {
        setCreateTarget(row);
        setCreateAccount(null);
        setForm({ amount: '', datePaid: '', paymentReference: '', notes: '' });
        setIsCreateModalOpen(true);
        setLoadingCreateAccount(true);
        try {
            const res = await reviewerPaymentService.getBankAccountDetailByUserId(row.reviewerId);
            if (res.isSuccess && res.data) {
                setCreateAccount(res.data);
            }
        } catch {
            /* account is optional for the form; ignore failures */
        } finally {
            setLoadingCreateAccount(false);
        }
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
        setCreateTarget(null);
        setCreateAccount(null);
        setForm({ amount: '', datePaid: '', paymentReference: '', notes: '' });
    };

    const handleCreatePayment = async () => {
        if (!createTarget) return;
        if (!createAccount?.accountNumber) {
            showNotification('error', {
                title: 'Account required',
                message: 'An account number has to be provided by the reviewer to create a record.',
            });
            return;
        }
        if (!form.amount || Number(form.amount) <= 0) {
            showNotification('error', { title: 'Validation', message: 'Please enter a valid amount.' });
            return;
        }
        if (!form.datePaid) {
            showNotification('error', { title: 'Validation', message: 'Please select the date paid.' });
            return;
        }

        setIsSaving(true);
        try {
            const res = await reviewerPaymentService.createUpdateReviewerPayment({
                id: ZERO_GUID,
                reviewAssignmentId: createTarget.reviewAssignmentId,
                amount: Number(form.amount),
                datePaid: new Date(form.datePaid).toISOString(),
                paymentReference: form.paymentReference.trim(),
                notes: form.notes.trim(),
                accountName: createAccount?.accountName ?? '',
                accountNumber: createAccount?.accountNumber ?? '',
                bankName: createAccount?.bankName ?? '',
            });

            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: res.message || 'Payment record created successfully.',
                });
                closeCreateModal();
                fetchPayments();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: res.message || 'Failed to create payment record.',
                });
            }
        } catch {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while creating the payment record.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    // ── View Payment Record ──────────────────────────────────────────────────

    const openViewModal = async (row: ReviewerPaymentItem) => {
        setViewTarget(row);
        setPaymentRecord(null);
        setIsViewModalOpen(true);
        setLoadingRecord(true);
        try {
            const res = await reviewerPaymentService.getReviewerPaymentByReviewAssignmentId(
                row.reviewAssignmentId
            );
            if (res.isSuccess && res.data) {
                setPaymentRecord(res.data);
            }
            // No record → the modal's empty-state text handles it (no popup).
        } catch {
            // Leave paymentRecord null so the empty-state text shows.
        } finally {
            setLoadingRecord(false);
        }
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setViewTarget(null);
        setPaymentRecord(null);
    };

    // ── Delete Payment Record ────────────────────────────────────────────────

    const openDeleteModal = async (row: ReviewerPaymentItem) => {
        setDeleteTarget(row);
        setDeleteRecord(null);
        setIsDeleteModalOpen(true);
        setLoadingDeleteRecord(true);
        try {
            // The table row only carries reviewAssignmentId; fetch the record to get its own id.
            const res = await reviewerPaymentService.getReviewerPaymentByReviewAssignmentId(
                row.reviewAssignmentId
            );
            if (res.isSuccess && res.data) {
                setDeleteRecord(res.data);
            }
            // No record → the modal's empty-state text handles it (no popup).
        } catch {
            // Leave deleteRecord null so the empty-state text shows.
        } finally {
            setLoadingDeleteRecord(false);
        }
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeleteTarget(null);
        setDeleteRecord(null);
    };

    const handleDeletePayment = async () => {
        if (!deleteRecord?.id) return;
        setIsDeleting(true);
        try {
            const res = await reviewerPaymentService.deleteReviewerPayment(deleteRecord.id);
            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: res.message || 'Payment record deleted successfully.',
                });
                closeDeleteModal();
                fetchPayments();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: res.message || 'Failed to delete payment record.',
                });
            }
        } catch {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while deleting the payment record.',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    // ── Helpers ──────────────────────────────────────────────────────────────

    const formatDate = (dateString?: string) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    };

    const formatCurrency = (amount?: number) =>
        `₦${Number(amount ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

    const getApplicationType = (type: number) => (type === 1 ? 'Book' : 'Journal Article');

    const renderAuthors = (row: ReviewerPaymentItem) => {
        if (row.bookAuthors && row.bookAuthors.length > 0) {
            return row.bookAuthors.map((a) => a?.name ?? '').filter(Boolean).join(', ');
        }
        return row.authorFullName?.trim() || '—';
    };

    const getPaymentBadge = (paid: string) => {
        const isPaid = paid?.toLowerCase() === 'paid';
        return (
            <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    isPaid
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}
            >
                {isPaid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                {paid || 'Unpaid'}
            </span>
        );
    };

    // ── Table columns ──────────────────────────────────────────────────────────

    const columns: TableColumn<ReviewerPaymentItem>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="text-gray-500 text-sm font-mono">{value}</span>,
        },
        {
            key: 'title',
            label: 'Title / Authors',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5">{row.title}</div>
                    <div className="text-sm text-gray-500">{renderAuthors(row)}</div>
                </div>
            ),
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value, row) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {row.categoryName || 'N/A'}
                </span>
            ),
        },
        {
            key: 'reviewerFullName',
            label: 'Reviewer',
            render: (value, row) => (
                <span className="text-sm text-gray-700">{row.reviewerFullName?.trim() || '—'}</span>
            ),
        },
        {
            key: 'paid',
            label: 'Payment Status',
            render: (value) => getPaymentBadge(value as string),
        },
    ];

    // ── Table actions ──────────────────────────────────────────────────────────

    const actions: TableAction<ReviewerPaymentItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Account Details',
                    onClick: openAccountModal,
                    className: 'text-blue-600',
                },
                {
                    label: 'Create Payment Record',
                    onClick: openCreateModal,
                    className: (row) =>
                        isDESS && row.paid?.toLowerCase() !== 'paid' ? 'text-[#088A61] font-semibold' : 'hidden',
                },
                {
                    label: 'View Payment Record',
                    onClick: openViewModal,
                    className: (row) => (row.paid?.toLowerCase() === 'paid' ? 'text-gray-700' : 'hidden'),
                },
                {
                    label: 'Delete Payment Record',
                    onClick: openDeleteModal,
                    className: (row) =>
                        isDESS && row.paid?.toLowerCase() === 'paid' ? 'text-red-600' : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Reviewer Payments
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Reviewers with completed evaluations that are ready for payment.
                    </p>
                </div>
            </div>

            {/* Table */}
            <ReusableTable
                title="Ready for Reviewer Payment"
                subTitle="Completed reviews awaiting payment processing"
                columns={columns}
                data={payments}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, reviewer, or ID..."
                onSearch={handleSearch}
                emptyMessage="No reviewer payments found."
                showSearch={true}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── View Account Details Modal ── */}
            <Modal
                isOpen={isAccountModalOpen}
                onClose={closeAccountModal}
                title="Bank Account Details"
                description="Reviewer's bank account information for payment."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {accountTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Reviewer
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                                {accountTarget.reviewerFullName?.trim() || '—'}
                            </p>
                        </div>
                    )}

                    {loadingAccount ? (
                        <div className="w-full px-3 py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                            <div className="w-5 h-5 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                            Loading account details...
                        </div>
                    ) : accountDetail ? (
                        <div className="space-y-3">
                            <div className="rounded-lg border border-gray-100 divide-y divide-gray-100">
                                <div className="px-4 py-3">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                                        Account Name
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">{accountDetail.accountName}</p>
                                </div>
                                <div className="px-4 py-3 flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                                            Account Number
                                        </p>
                                        <p className="text-sm font-mono font-semibold text-gray-900 tracking-wide">
                                            {accountDetail.accountNumber}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(accountDetail.accountNumber)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                                        title="Copy account number"
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-3.5 h-3.5 text-green-600" /> Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-3.5 h-3.5" /> Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="px-4 py-3">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                                        Bank Name
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">{accountDetail.bankName}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full px-3 py-6 border border-gray-100 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                            No bank account details available.
                        </div>
                    )}

                    <div className="flex items-center justify-end pt-2">
                        <button
                            onClick={closeAccountModal}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Create Payment Record Modal ── */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={closeCreateModal}
                title="Create Payment Record"
                description="Record a payment made to the reviewer for this completed review."
                maxWidth="lg"
            >
                <div className="space-y-4 mt-2">
                    {createTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Submission
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{createTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{createTarget.submissionNumber}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Reviewer: <span className="font-medium">{createTarget.reviewerFullName?.trim()}</span>
                            </p>
                        </div>
                    )}

                    {/* Account details on top */}
                    {loadingCreateAccount ? (
                        <div className="w-full px-3 py-4 flex items-center justify-center gap-2 text-sm text-gray-400 border border-gray-100 rounded-lg bg-gray-50">
                            <div className="w-4 h-4 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                            Loading account details...
                        </div>
                    ) : createAccount ? (
                        <div className="rounded-lg border border-[#088A61]/20 bg-[#088A61]/5 px-4 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Account Name</p>
                                    <p className="text-sm font-medium text-gray-900">{createAccount.accountName}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Account Number</p>
                                    <p className="text-sm font-mono font-semibold text-gray-900">
                                        {createAccount.accountNumber}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Bank</p>
                                    <p className="text-sm font-medium text-gray-900">{createAccount.bankName}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full px-3 py-3 border border-amber-100 rounded-lg bg-amber-50 text-xs text-amber-700 text-center italic">
                            An account number has to be provided by the reviewer before a payment record can be created.
                        </div>
                    )}

                    {/* Form */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Amount <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={form.amount}
                                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                                placeholder="0.00"
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Date Paid <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={form.datePaid}
                                onChange={(e) => setForm((f) => ({ ...f, datePaid: e.target.value }))}
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Payment Reference
                        </label>
                        <input
                            type="text"
                            value={form.paymentReference}
                            onChange={(e) => setForm((f) => ({ ...f, paymentReference: e.target.value }))}
                            placeholder="e.g. transaction reference / receipt no."
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Notes <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            placeholder="Add any notes about this payment..."
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeCreateModal}
                            disabled={isSaving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreatePayment}
                            disabled={isSaving || !createAccount?.accountNumber}
                            title={!createAccount?.accountNumber ? 'The reviewer must provide an account number first' : undefined}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSaving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Save Payment
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── View Payment Record Modal ── */}
            <Modal
                isOpen={isViewModalOpen}
                onClose={closeViewModal}
                title="Payment Record"
                description="Details of the payment recorded for this review."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {viewTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Submission
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{viewTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{viewTarget.submissionNumber}</p>
                        </div>
                    )}

                    {loadingRecord ? (
                        <div className="w-full px-3 py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                            <div className="w-5 h-5 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                            Loading payment record...
                        </div>
                    ) : paymentRecord ? (
                        <div className="space-y-3">
                            <div className="rounded-lg border border-gray-100 divide-y divide-gray-100">
                                <div className="px-4 py-3 flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                                        <Banknote className="w-3.5 h-3.5" /> Amount
                                    </span>
                                    <span className="text-base font-bold text-[#088A61]">
                                        {formatCurrency(paymentRecord.amount)}
                                    </span>
                                </div>
                                <div className="px-4 py-3 flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                        Date Paid
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {formatDate(paymentRecord.datePaid)}
                                    </span>
                                </div>
                                <div className="px-4 py-3 flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                        Payment Reference
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {paymentRecord.paymentReference || '—'}
                                    </span>
                                </div>
                                <div className="px-4 py-3">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                                        <User className="w-3.5 h-3.5" /> Account
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {paymentRecord.accountName || '—'}
                                    </p>
                                    <p className="text-sm font-mono text-gray-700">
                                        {paymentRecord.accountNumber || '—'}
                                    </p>
                                    <p className="text-sm text-gray-700 mt-0.5">
                                        {paymentRecord.bankName || '—'}
                                    </p>
                                </div>
                                {paymentRecord.notes && (
                                    <div className="px-4 py-3">
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                                            <FileText className="w-3.5 h-3.5" /> Notes
                                        </p>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {paymentRecord.notes}
                                        </p>
                                    </div>
                                )}
                                {paymentRecord.enteredByUser?.person?.fullName && (
                                    <div className="px-4 py-3 flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                            Entered By
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">
                                            {paymentRecord.enteredByUser.person.fullName}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="w-full px-3 py-6 border border-gray-100 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                            No payment record found.
                        </div>
                    )}

                    <div className="flex items-center justify-end pt-2">
                        <button
                            onClick={closeViewModal}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Delete Payment Record Modal ── */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                title="Delete Payment Record"
                description="This will permanently remove the payment record for this review. This action cannot be undone."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {deleteTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Submission
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{deleteTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{deleteTarget.submissionNumber}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Reviewer: <span className="font-medium">{deleteTarget.reviewerFullName?.trim()}</span>
                            </p>
                        </div>
                    )}

                    {loadingDeleteRecord ? (
                        <div className="w-full px-3 py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                            <div className="w-5 h-5 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
                            Loading payment record...
                        </div>
                    ) : deleteRecord ? (
                        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 flex items-center justify-between">
                            <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Amount</span>
                            <span className="text-base font-bold text-red-700">
                                {formatCurrency(deleteRecord.amount)}
                            </span>
                        </div>
                    ) : (
                        <div className="w-full px-3 py-6 border border-gray-100 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                            No payment record found to delete.
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeDeleteModal}
                            disabled={isDeleting}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeletePayment}
                            disabled={isDeleting || !deleteRecord?.id}
                            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isDeleting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    Delete Record
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default ReviewerPayment;
